import { useEffect, useId, useMemo, useRef, useState } from 'react';

import type {
  ImportField,
  ImportMatchResult,
  ImportProgress,
  ImportSubsetResult,
  SelectiveImportSession,
} from '../data/import/importPreflight';
import type { FieldDefinition } from '../data/model';
import {
  attributeRuleOperatorDefinition,
  attributeRuleOperatorsForField,
  type AttributeRuleOperatorDefinition,
} from '../rules/operators';
import type { AttributeRuleCondition, AttributeRuleOperator } from '../rules/model';
import { DialogShell } from './DialogShell';
import { ImportMetadata } from './ImportMetadata';

interface SelectiveImportDialogProps {
  readonly session: SelectiveImportSession;
  readonly onClose: () => void;
  readonly onBack?: () => void;
  readonly onImport: (
    subset: ImportSubsetResult,
    options: { readonly signal: AbortSignal; readonly onProgress: (value: ImportProgress) => void },
  ) => Promise<void>;
  readonly returnFocusTo?: HTMLElement | null | undefined;
}

function fieldDefinition(field: ImportField, featureCount: number): FieldDefinition {
  return {
    name: field.name,
    type: field.type,
    nullable: true,
    presentCount: featureCount,
    nullCount: 0,
  };
}

function operatorsForField(
  field: ImportField,
  featureCount: number,
): readonly AttributeRuleOperatorDefinition[] {
  const definition = fieldDefinition(field, featureCount);
  const operators = attributeRuleOperatorsForField(definition);
  return field.displayType === 'Date'
    ? operators.filter((operator) =>
        ['equals', 'does-not-equal', 'is-empty', 'is-not-empty'].includes(operator.operator),
      )
    : operators;
}

function firstCondition(field: ImportField, featureCount: number): AttributeRuleCondition {
  const definition = operatorsForField(field, featureCount)[0];
  const operator = definition?.operator ?? 'equals';
  return {
    kind: 'condition',
    field: field.name,
    operator,
    value: definition?.valueKind === 'number' ? 0 : definition?.valueKind === 'string' ? '' : null,
  };
}

function fieldExample(field: ImportField): string {
  if (field.sampleValue === undefined) return '—';
  if (typeof field.sampleValue === 'boolean') return field.sampleValue ? 'True' : 'False';
  return String(field.sampleValue);
}

function progressText(progress: ImportProgress | null): string | null {
  if (!progress) return null;
  const label =
    progress.phase === 'finding-matches'
      ? 'Finding matches'
      : progress.phase === 'importing-features'
        ? 'Importing selected features'
        : progress.phase === 'reprojecting'
          ? 'Reprojecting'
          : progress.phase === 'reading-attributes'
            ? 'Reading attributes'
            : 'Inspecting dataset';
  return progress.total && progress.completed !== undefined
    ? `${label}… ${progress.completed.toLocaleString()} of ${progress.total.toLocaleString()}`
    : `${label}…`;
}

export function SelectiveImportDialog({
  session,
  onClose,
  onImport,
  returnFocusTo,
  onBack,
}: SelectiveImportDialogProps) {
  const featureCount = session.preflight.featureCount ?? 0;
  const fields = session.preflight.fields;
  const [condition, setCondition] = useState<AttributeRuleCondition | null>(() =>
    fields[0] ? firstCondition(fields[0], featureCount) : null,
  );
  const [matches, setMatches] = useState<ImportMatchResult | null>(null);
  const [selectedFields, setSelectedFields] = useState<readonly string[]>(() =>
    fields.map((field) => field.name),
  );
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const controllerRef = useRef<AbortController | null>(null);
  const titleId = useId();
  const fieldMetadataId = useId();
  const selectedField = useMemo(
    () => fields.find((field) => field.name === condition?.field),
    [condition?.field, fields],
  );
  const selectedDefinition = selectedField
    ? fieldDefinition(selectedField, featureCount)
    : undefined;
  const operatorDefinition =
    selectedDefinition && condition
      ? attributeRuleOperatorDefinition(selectedDefinition, condition.operator)
      : null;
  const availableOperators = selectedField ? operatorsForField(selectedField, featureCount) : [];
  const subset = useMemo(
    () => (matches ? session.planSubset(matches, selectedFields) : null),
    [matches, selectedFields, session],
  );
  const resultMessage =
    matches?.matchCount === 0
      ? 'No features match this filter.'
      : subset?.safety === 'too-large'
        ? (subset.safetyMessage ??
          'This filter and attribute selection still contain too much data to import safely. Narrow the filter or import fewer attributes.')
        : null;

  useEffect(() => () => controllerRef.current?.abort(), []);

  const invalidate = (next: AttributeRuleCondition) => {
    setCondition(next);
    setMatches(null);
    setMessage(null);
  };

  const cancel = () => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    onClose();
  };

  const findMatches = async () => {
    if (!condition) return;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setWorking(true);
    setMatches(null);
    setMessage(null);
    setProgress({ phase: 'finding-matches', completed: 0, total: featureCount });
    try {
      const result = await session.findMatches(
        { conditions: [condition] },
        { signal: controller.signal, onProgress: setProgress },
      );
      if (controller.signal.aborted) return;
      setMatches(result);
    } catch (error) {
      if (!controller.signal.aborted) {
        setMessage(
          error instanceof Error ? error.message : 'Placeio Maps could not scan these attributes.',
        );
      }
    } finally {
      if (controllerRef.current === controller) controllerRef.current = null;
      if (!controller.signal.aborted) {
        setWorking(false);
        setProgress(null);
      }
    }
  };

  const importSelected = async () => {
    if (!subset || subset.matchCount === 0 || subset.safety !== 'safe') return;
    const controller = new AbortController();
    controllerRef.current = controller;
    setWorking(true);
    setMessage(null);
    setProgress({ phase: 'importing-features', completed: 0, total: subset.matchCount });
    try {
      await onImport(subset, { signal: controller.signal, onProgress: setProgress });
    } catch (error) {
      if (!controller.signal.aborted) {
        setMessage(
          error instanceof Error ? error.message : 'Placeio Maps could not import this subset.',
        );
      }
    } finally {
      if (controllerRef.current === controller) controllerRef.current = null;
      if (!controller.signal.aborted) {
        setWorking(false);
        setProgress(null);
      }
    }
  };

  return (
    <DialogShell
      className="new-map-dialog selective-import-dialog"
      onClose={cancel}
      returnFocusTo={returnFocusTo}
      titleId={titleId}
    >
      <h2 id={titleId}>Import by attribute</h2>
      <ImportMetadata
        file={session.preflight.sourceFileName}
        layers={[session.preflight.suggestedLayerName]}
        count={session.preflight.featureCount ?? null}
        scope="Attribute filter"
      />
      {session.preflight.fullImportSafety === 'SELECTIVE_ONLY' ? (
        <p className="style-notice">This dataset is too large to import safely in full.</p>
      ) : null}
      <fieldset disabled={working || fields.length === 0}>
        <legend>Filter</legend>
        <label>
          Field
          <select
            aria-describedby={fieldMetadataId}
            aria-label="Import field"
            onChange={(event) => {
              const field = fields.find((candidate) => candidate.name === event.target.value);
              if (field) invalidate(firstCondition(field, featureCount));
            }}
            value={condition?.field ?? ''}
          >
            {fields.map((field) => (
              <option key={field.name} value={field.name}>
                {field.name}
              </option>
            ))}
          </select>
        </label>
        {selectedField ? (
          <p
            aria-label={`Type: ${selectedField.displayType}; Example: ${fieldExample(selectedField)}`}
            className="selective-import-field-metadata"
            id={fieldMetadataId}
          >
            <span>Type: {selectedField.displayType}</span>
            <span aria-hidden="true" className="selective-import-field-metadata-separator">
              |
            </span>
            <span className="selective-import-field-example">
              Example:{' '}
              <span title={fieldExample(selectedField)}>{fieldExample(selectedField)}</span>
            </span>
          </p>
        ) : null}
        <label>
          Operator
          <select
            aria-label="Import operator"
            onChange={(event) => {
              if (!condition || !selectedDefinition) return;
              const operator = event.target.value as AttributeRuleOperator;
              const definition = attributeRuleOperatorDefinition(selectedDefinition, operator);
              if (!definition) return;
              invalidate({
                ...condition,
                operator,
                value:
                  definition.valueKind === 'number'
                    ? 0
                    : definition.valueKind === 'string'
                      ? ''
                      : null,
              });
            }}
            value={condition?.operator ?? ''}
          >
            {availableOperators.map((operator) => (
              <option key={operator.operator} value={operator.operator}>
                {operator.label}
              </option>
            ))}
          </select>
        </label>
        {operatorDefinition?.valueKind === 'number' ? (
          <label>
            Value
            <input
              aria-label="Import value"
              onChange={(event) => {
                const value = Number(event.target.value);
                if (condition && Number.isFinite(value)) invalidate({ ...condition, value });
              }}
              type="number"
              value={typeof condition?.value === 'number' ? condition.value : 0}
            />
          </label>
        ) : operatorDefinition?.valueKind === 'string' ? (
          <label>
            Value
            <input
              aria-label="Import value"
              onChange={(event) =>
                condition && invalidate({ ...condition, value: event.target.value })
              }
              type={selectedField?.displayType === 'Date' ? 'date' : 'text'}
              value={typeof condition?.value === 'string' ? condition.value : ''}
            />
          </label>
        ) : null}
        <button
          className="secondary-action"
          disabled={!condition || working}
          onClick={() => void findMatches()}
          type="button"
        >
          Find matches
        </button>
      </fieldset>
      {progressText(progress) ? (
        <p className="import-status" role="status">
          {progressText(progress)}
        </p>
      ) : null}
      {matches && matches.matchCount > 0 ? (
        <p className="attribute-query-count" role="status">
          {matches.matchCount.toLocaleString()} of {matches.totalFeatureCount.toLocaleString()}{' '}
          features match
        </p>
      ) : null}
      {message || resultMessage ? (
        <p className="import-error" role="alert">
          {message ?? resultMessage}
        </p>
      ) : null}
      <fieldset className="selective-import-attributes" disabled={working}>
        <legend>Attributes to import</legend>
        <details>
          <summary
            aria-label={`Attributes to import, ${selectedFields.length} of ${fields.length} selected`}
          >
            {selectedFields.length} of {fields.length} selected
          </summary>
          <div className="selective-import-attribute-picker">
            <div className="selective-import-attribute-actions">
              <button
                disabled={selectedFields.length === fields.length}
                onClick={() => setSelectedFields(fields.map((field) => field.name))}
                type="button"
              >
                Select all
              </button>
              <button
                disabled={selectedFields.length === 0}
                onClick={() => setSelectedFields([])}
                type="button"
              >
                Clear all
              </button>
            </div>
            <div className="selective-import-attribute-list">
              <table aria-label="Source attributes to import">
                <thead>
                  <tr>
                    <th aria-label="Selected" />
                    <th scope="col">Attribute</th>
                    <th scope="col">Type</th>
                    <th scope="col">Example</th>
                  </tr>
                </thead>
                <tbody>
                  {fields.map((field, index) => {
                    const checkboxId = `${titleId}-attribute-${index}`;
                    const example = fieldExample(field);
                    return (
                      <tr key={field.name}>
                        <td>
                          <input
                            checked={selectedFields.includes(field.name)}
                            id={checkboxId}
                            onChange={(event) =>
                              setSelectedFields((current) =>
                                event.target.checked
                                  ? fields
                                      .filter(
                                        (candidate) =>
                                          candidate.name === field.name ||
                                          current.includes(candidate.name),
                                      )
                                      .map((candidate) => candidate.name)
                                  : current.filter((name) => name !== field.name),
                              )
                            }
                            type="checkbox"
                          />
                        </td>
                        <th scope="row">
                          <label htmlFor={checkboxId} title={field.name}>
                            {field.name}
                          </label>
                        </th>
                        <td className="selective-import-attribute-type">{field.displayType}</td>
                        <td className="selective-import-attribute-example" title={example}>
                          {example}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </details>
      </fieldset>
      <div className="import-dialog-actions">
        {onBack ? (
          <button type="button" disabled={working} onClick={onBack}>
            Change method
          </button>
        ) : null}
        <button onClick={cancel} type="button">
          Cancel
        </button>
        <button
          className="primary-action"
          disabled={!subset || subset.matchCount === 0 || subset.safety !== 'safe' || working}
          onClick={() => void importSelected()}
          type="button"
        >
          {subset?.matchCount
            ? `Import ${subset.matchCount.toLocaleString()} features`
            : 'Import selected features'}
        </button>
      </div>
    </DialogShell>
  );
}
